import React from 'react';
import { Sequence } from 'remotion';
import { resolveGroupGeometry } from '../items/group/group-geometry';
import { EditorStarterItem } from '../items/item-type';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { getRectAfterCrop } from '../utils/get-dimensions-after-crop';
import { useAllItems, useSelectedItems } from '../utils/use-context';

/**
 * inspectedGroupMemberId 멤버의 bbox 에 selected 와 같은 계열 outline 만 그린다.
 * getRectAfterCrop(절대좌표) + scale 보정 border 는 SelectionOutline 방식을 그대로 미러.
 */
const InspectedMemberOutlineBox: React.FC<{ item: EditorStarterItem }> = ({
  item,
}) => {
  const scale = useCanvasTransformationScale();
  // SelectionOutline 과 동일하게 기본 border 2px 를 캔버스 스케일로 보정한다.
  const border = Math.ceil(2 / scale);
  const rect = getRectAfterCrop(item);
  const hasRotation = 'rotation' in item;

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: Math.round(rect.left),
        top: Math.round(rect.top),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        outline: `${border}px solid var(--color-editor-starter-accent)`,
        transform: hasRotation ? `rotate(${item.rotation}deg)` : undefined,
        // 표시 전용 — 히트테스팅·드래그 경로를 건드리지 않아 개별 이동이 불가능하다.
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    />
  );
};

/**
 * 1급 GroupItem inspector 에서 멤버로 depth 진입했을 때, canvas 에서도 그 멤버가
 * "선택된 것처럼" 보이도록 outline 만 그리는 표시 전용 오버레이.
 *
 * - 개별 드래그/리사이즈는 허용하지 않는다: 리사이즈 핸들 없음 + pointer-events:none.
 * - selection-outline.tsx(선택/드래그/히트테스팅) 로직은 수정하지 않고, 좌표 변환·프레임
 *   게이팅 방식만 미러해 마운트 지점에 나란히 추가한다.
 * - 표시 조건: 단독 선택된 아이템이 그 멤버를 children 으로 가진 그룹일 때만(잔존 값 방어).
 */
export const InspectedMemberOutline: React.FC = () => {
  const { items } = useAllItems();
  const { selectedItems, inspectedGroupMemberId } = useSelectedItems();

  if (inspectedGroupMemberId == null) return null;
  // 1급 그룹이 단독 선택된 컨텍스트에서만 표시한다(다른 선택으로 넘어간 뒤 잔존한 값 방어).
  if (selectedItems.length !== 1) return null;

  const groupId = selectedItems[0];
  if (groupId == null) return null;
  const group = items[groupId];
  if (!group || group.type !== 'group') return null;
  if (!group.children.includes(inspectedGroupMemberId)) return null;

  const member = items[inspectedGroupMemberId];
  if (!member) return null;

  const groupGeometry = resolveGroupGeometry(group, items);

  // 그룹 아이템이 현재 프레임에 표시 중일 때만 그린다(그룹 자체 selection outline 과 타이밍
  // 일치). children 은 그룹 Sequence 창 동안 함께 렌더되므로 그룹 from/duration 을 쓴다.
  return (
    <Sequence
      from={groupGeometry.from}
      durationInFrames={groupGeometry.durationInFrames}
      layout="none"
    >
      <InspectedMemberOutlineBox item={member} />
    </Sequence>
  );
};
